import { type FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import CloseIcon from '@/assets/svg/ui-elements/close-outline.svg';
import InfoIcon from '@/assets/svg/ui-elements/info.svg';

import Button, { IconButton } from '../Button';
import type { ButtonColor, ButtonVariant } from '../Button/types';
import {
  StyledAlert,
  StyledContent,
  StyledDescription,
  StyledIconWrapper,
  StyledText,
  StyledTitle,
} from './Alert.styled';
import type { AlertProps, AlertVariant } from './types';

const ACTION_STYLE: Record<AlertVariant, { variant: ButtonVariant; color: ButtonColor }> = {
  default: { variant: 'outline', color: 'primary' },
  success: { variant: 'solid', color: 'primary' },
  warning: { variant: 'solid', color: 'destructive' },
  error: { variant: 'solid', color: 'destructive' },
  info: { variant: 'solid', color: 'secondary' },
};

const Alert: FC<AlertProps> = ({
  variant = 'default',
  size = 'lg',
  title,
  description,
  icon,
  showIcon = true,
  action,
  onClose,
  ...rest
}) => (
  <StyledAlert
    $variant={variant}
    $size={size}
    role={variant === 'error' ? 'alert' : 'status'}
    {...rest}
  >
    {showIcon && (
      <StyledIconWrapper $variant={variant} $hasLargeTitle={Boolean(title) && size === 'lg'}>
        {icon ?? <InfoIcon />}
      </StyledIconWrapper>
    )}

    <StyledContent>
      {(title || description) && (
        <StyledText>
          {title && <StyledTitle $size={size}>{title}</StyledTitle>}
          {description && <StyledDescription>{description}</StyledDescription>}
        </StyledText>
      )}

      {action && (
        <Button
          size="sm"
          variant={ACTION_STYLE[variant].variant}
          color={ACTION_STYLE[variant].color}
          onClick={action.onClick}
          {...(action.to !== undefined && {
            nativeButton: false,
            role: 'link',
            render: <RouterLink to={action.to} />,
          })}
        >
          {action.label}
        </Button>
      )}
    </StyledContent>

    {onClose && (
      <IconButton aria-label="Dismiss" variant="ghost" color="tertiary" size="xs" onClick={onClose}>
        <CloseIcon />
      </IconButton>
    )}
  </StyledAlert>
);

Alert.displayName = 'Alert';

export default Alert;
